import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AssetNavigationService } from '../../../services/asset-navigation.service';
import { AuthService } from '../../../services/auth.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { LoadingService } from '../../../services/loading.service';
import { PermissionService } from '../../../services/permission.service';
import { UserImpersonationService } from '../../../services/user-impersonation.service';
import { AssetStore } from '../../../stores/asset.store';
import { UserStore } from '../../../stores/user.store';
import { Asset } from '../../../types/asset.interface';
import { CesFavoriteButtonComponent } from '../../ui/ces-buttons';
import { CesCardCarouselComponent } from '../../ui/ces-card-carousel';
import { CesCard } from '../../ui/ces-card/ces-card.component';

@Component({
  selector: 'app-home',
  imports: [CommonModule, RouterModule, CesCard, CesCardCarouselComponent, TranslocoModule, CesFavoriteButtonComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent {
  private readonly assetStore = inject(AssetStore);
  protected readonly loadingService = inject(LoadingService);
  private readonly assetNavigationService = inject(AssetNavigationService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly userStore = inject(UserStore);
  private readonly permissionService = inject(PermissionService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly accessibleAssetIds = signal<ReadonlySet<number> | 'all'>(new Set());

  constructor() {
    effect(() => {
      this.assetStore.entities();
      this.authService.currentUserId();
      this.impersonationService.isImpersonating();
      void this.refreshAccessibleAssets();
    });
  }

  protected readonly assets = computed(() => {
    const allAssets = [...this.assetStore.entities()]
      .filter((asset: Asset) => asset.lastAccess != null)
      .sort(this.sortByLastAccessDesc);
    return this.filterAssetsByPermissions(allAssets).slice(0, 10);
  });

  protected readonly assetsFavorite = computed(() => {
    const allAssets = [...this.assetStore.entities()].filter((asset: Asset) => asset.isFavorite).sort(this.sortByLastAccessDesc);
    return this.filterAssetsByPermissions(allAssets).slice(0, 10);
  });

  private sortByLastAccessDesc = (a: Asset, b: Asset): number => {
    if (a.lastAccess == null) return 1;
    if (b.lastAccess == null) return -1;
    return new Date(b.lastAccess).getTime() - new Date(a.lastAccess).getTime();
  };

  private filterAssetsByPermissions(assets: Asset[]): Asset[] {
    const allowed = this.accessibleAssetIds();
    if (allowed === 'all') {
      return assets;
    }
    return assets.filter(asset => allowed.has(asset.id));
  }

  private async refreshAccessibleAssets(): Promise<void> {
    const allAssets = this.assetStore.entities();

    if (this.authorizationService.isAdminOrDev()) {
      this.accessibleAssetIds.set('all');
      return;
    }

    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);
    if (!effectiveUserId) {
      this.accessibleAssetIds.set(new Set());
      return;
    }

    if (!this.impersonationService.isImpersonating()) {
      const user = this.userStore.entities().find(u => u.id === effectiveUserId);
      if (user?.isAdmin) {
        this.accessibleAssetIds.set('all');
        return;
      }
    }

    this.accessibleAssetIds.set(new Set());

    const checks = await Promise.all(
      allAssets.map(async asset => ({
        id: asset.id,
        accessible: (await this.permissionService.getFirstAuthorizedRoute(effectiveUserId, asset.id)) !== null,
      })),
    );

    this.accessibleAssetIds.set(new Set(checks.filter(c => c.accessible).map(c => c.id)));
    this.cdr.markForCheck();
  }

  navigate(id: number): void {
    void this.assetNavigationService.navigateToAsset(id);
  }

  getAssetHref(id: number): string {
    return `/assets/${id}`;
  }

  async toggleFavorite(asset: Asset) {
    const userId = this.authService.currentUserId();

    if (!userId) return;

    const newFavoriteState = !asset.isFavorite;

    await this.assetStore.update(asset.id, { isFavorite: newFavoriteState });
    this.cdr.markForCheck();

    try {
      await this.userStore.updateUserAssetSetting(userId, asset.id, { favorite: newFavoriteState });
    } catch {
      await this.assetStore.update(asset.id, { isFavorite: !newFavoriteState });
      this.cdr.markForCheck();
    }
  }
}
